import type { Angabe, ProjektSteckbrief } from '../../../services/projektSteckbrief/steckbrief';
import type { Handbuch } from '../../handbuch/types';
import type { ProjektMerkmale } from '../../planungsphase/types';
import type { NotrufplanInhalt } from '../../sige-plan/types';
import { istBestaetigt, naechsteLueckenNummer, offenePlatzhalterIn, platzhalterIn } from '../bauordnungLogic';
import { kapitelDefinition, kapitelTrifftZu, type BooleanMerkmal } from '../kapitel';
import type { Bauordnung, BauordnungBundle, ErlassenderRolle, Luecke, Regel, RegelArt, Rolle } from '../types';
import {
    HANDBUCH_FELDER, uebernahmeAusHandbuch, type HandbuchFeld, type HandbuchUebernahme,
} from '../uebernahmeHandbuch';
import { grundlagenVorschlaege } from '../vorschlaege';
import { KATALOG, KATALOG_VERSION } from './katalog';
import {
    KOPF_FELD_JE_PLATZHALTER, istDynamisch, kurzZuName, nameZuKurz, platzhalterDefinition, type KopfFeld,
} from './platzhalter';
import type { Baustein } from './typen';

/**
 * Der Automatik-Lauf der Baustellenordnung (Plan
 * docs/baustellenordnung-assistenz-plan.md §3.1, Stufe B1).
 *
 * Leitentscheid des Betreibers 02.11.2026: „So automatisiert wie nur moeglich;
 * die Mitarbeiter sollen so wenig wie moeglich machen." Der Entwurf baut sich
 * deshalb selbst: Kopfsatz aus dem Steckbrief, alle zutreffenden Bausteine
 * des Katalogs als BESTAETIGTE Regeln (E2), Organisation aus den
 * Projektbeteiligten (E3), Uebernahme aus dem Handbuch, sobald es dort etwas
 * gibt (E7), und fuer jeden Platzhalter, den kein Projektdatum fuellt, ein
 * offener Punkt. Der Mensch beantwortet nur noch die echten Luecken und gibt
 * frei.
 *
 * ═══ KOPF-PLATZHALTER BLEIBEN STEHEN ══════════════════════════════════════
 * `{erlassender}`, `{stand_datum}`, `{revision_nr}` und die anderen
 * dynamischen Platzhalter (`platzhalter.ts`) setzt der Lauf NICHT ein: sie
 * bleiben als `{name}` im Wortlaut, ohne offenen Punkt, und `baueDokument`
 * loest sie beim Druck aus dem Kopfsatz auf (`loeseKopfPlatzhalter`). Fest
 * eingesetzt stuende nach „Neue Fassung eröffnen" noch „Revision 00" im
 * Text, nach einem Wechsel des Erlassenden noch der alte.
 *
 * ═══ RECHNEN HIER, SCHREIBEN IM DIENST ════════════════════════════════════
 * Diese Datei ist rein: sie liefert ein Paket, `bauordnungService.
 * fuehreAutomatikAus` schreibt es in EINER Folge. So ist jede Entscheidung
 * (welcher Baustein, welcher Wert, welcher offene Punkt) ohne Datenbank
 * pruefbar.
 *
 * ═══ IDEMPOTENZ IST DER SCHUTZ (F14) ══════════════════════════════════════
 * Der Lauf wird beim Oeffnen wiederholt — nach einem Fehlschlag, nach einer
 * neuen Fassung, nach geaenderten Merkmalen. Nichts darf doppelt entstehen:
 *   Regeln   je (Kapitel, `baustein_id`) einmal
 *   Rollen   je Rolle + Firma einmal
 *   Luecken  je (Regel, Platzhalter) eine offene; je Merkmal eine
 *   Handbuch nur ohne `uebernommen_handbuch_am`
 * Dasselbe Paket zweimal angewandt ergibt beim zweiten Mal `leer: true`.
 *
 * ═══ MARKER IN DER HISTORIE (F13) ═════════════════════════════════════════
 * Es gibt keine Spalte fuer „Automatik gelaufen" (kein SQL). Der Nachweis ist
 * ein Historie-Eintrag, dessen Text mit `automatik:<Katalog>:<Fassung>`
 * beginnt; die KI schreibt `ki:<Fassung>`, eine Loeschung in „Alle Bereiche"
 * `abgewaehlt:<baustein_id>` (F15). Der Fassungsindex zaehlt die Eintraege
 * „Neue Fassung eröffnet" — `revisionEroeffnen` bleibt auf derselben Zeile,
 * ohne den Index liefe die KI fuer Rev. 01 nie wieder.
 *
 * ═══ WAS NIE GESCHIEHT ════════════════════════════════════════════════════
 *   - Stufe `optional` wird nie geschrieben, ein unbekanntes Merkmal nie
 *     geraten (Luecke „Merkmal klären" statt Regel);
 *   - Quelle `vorgabe`/`mensch` fuellt keinen Platzhalter — den Vorschlag
 *     bestaetigt der Mensch auf der Karte;
 *   - nur `belegt`e Steckbrief-Angaben, nie eine Ersatzquelle;
 *   - kein Feld des Kopfsatzes wird ueberschrieben, nur leere gefuellt;
 *   - an einer freigegebenen Fassung wird nichts gerechnet (leeres Paket).
 */

// ═══════════════════════════════════════════════════════════════════════════
// TYPEN
// ═══════════════════════════════════════════════════════════════════════════

export interface AutomatikQuellen {
    ordnung: Bauordnung;
    bundle: BauordnungBundle;
    merkmale: ProjektMerkmale;
    steckbrief: ProjektSteckbrief | null;
    /** Das gelesene Handbuch — nur die Ordnungsfelder und das Modell werden gebraucht. */
    handbuch: Pick<Handbuch, 'ordnung' | 'org_modell'> | null;
    notrufplan: NotrufplanInhalt | null;
    /** Das eigene Buero: Firma aus dem Profil, Handelnder = wer den Lauf ausloest. */
    buero: { firma: string; handelnder: string };
    projekt: { name: string; adresse: string };
    /** Heutiger Tag in Ortszeit, YYYY-MM-DD. */
    heuteIso: string;
    /** Vorgabe `KATALOG` — Tests reichen eigene Bausteine. */
    katalog?: readonly Baustein[];
}

export interface NeueRegel {
    kapitel: string;
    baustein_id: string;
    wortlaut: string;
    art: RegelArt;
    quelle: 'katalog';
    sortierung: number;
    /**
     * Namen der Platzhalter, die kein Projektdatum fuellte — stehen als
     * `[Kurz]` im Wortlaut. Die dynamischen des Kopfsatzes fehlen hier: sie
     * bleiben `{name}` und werden beim Druck aufgeloest.
     */
    offenePlatzhalter: string[];
}

export interface NeueRolle {
    rolle: string;
    funktion: string;
    firma: string;
    name: string;
    erreichbarkeit: string;
    weisungsbefugnis: 'keine';
    befugnis_umfang: '';
    bestellt_am: null;
    sortierung: number;
}

export interface NeueLuecke {
    kapitel: string;
    punkt: string;
    /** `platzhalter:<name>`, `merkmal:<merkmal>` oder `kopf:erlassender`. */
    verweis: string;
    pflicht: true;
    herkunft: 'automatisch';
    platzhalter?: string;
    /** Fuer Luecken an NEUEN Regeln: der Dienst ordnet nach dem Einfuegen die `regel_id` zu. */
    bausteinId?: string;
    /** Fuer Luecken an BESTEHENDEN Regeln. */
    regelId?: string;
    merkmal?: BooleanMerkmal;
}

export interface Klaerung {
    merkmal: BooleanMerkmal;
    bausteine: Baustein[];
}

export interface BausteinAuswahl {
    /** Werden als bestaetigte Regeln geschrieben. */
    schreiben: Baustein[];
    /** Bedingung mit unbekanntem Merkmal — je Merkmal eine Frage. Ein Baustein mit
     *  zwei unbekannten Merkmalen (ODER) steht in beiden Gruppen. */
    klaeren: Klaerung[];
    /** Fuer die aufklappbare Liste „nicht aufgenommen" — mit Grund. */
    weggelassen: { baustein: string; grund: string }[];
}

export interface AutomatikPaket {
    /** Nur leere Felder; nie `historie` (die schreibt der Dienst zuletzt). */
    kopfPatch: Partial<Bauordnung>;
    /** Uebernahme aus dem Handbuch — nur, wenn es Ordnungsfelder mit Inhalt fuehrt (E7). */
    handbuch: HandbuchUebernahme | null;
    regeln: NeueRegel[];
    rollen: NeueRolle[];
    /** Neue offene Punkte — an neuen Regeln, an bestehenden, Merkmale, Erlassender. */
    luecken: NeueLuecke[];
    /** Ids offener Punkte, deren Platzhalter nicht mehr im Text steht — werden erledigt, nicht geloescht. */
    erledigen: string[];
    /** Erste freie Nummer fuer `luecken` (Nummern werden nie neu vergeben). */
    ersteLueckenNr: number;
    klaeren: { merkmal: BooleanMerkmal; bausteine: string[] }[];
    weggelassen: { baustein: string; grund: string }[];
    zaehler: { regeln: number; rollen: number; luecken: number; klaeren: number; handbuchZeilen: number };
    /** Nichts zu schreiben (den Marker ausgenommen). */
    leer: boolean;
    /** Der Historie-Eintrag — beginnt mit dem Marker, solange er fehlt. */
    historie: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// MARKER (F13, F15) — die Konstanten an EINER Stelle
// ═══════════════════════════════════════════════════════════════════════════

/** Wortgleich mit dem Eintrag von `bauordnungService.revisionEroeffnen`. */
export const NEUE_FASSUNG_TEXT = 'Neue Fassung eröffnet';
export const AUTOMATIK_PRAEFIX = 'automatik:';
export const KI_PRAEFIX = 'ki:';
export const ABGEWAEHLT_PRAEFIX = 'abgewaehlt:';
/** So steht die Automatik als Bestaetigende an ihren Regeln. */
export const AUTOMATIK_KATALOG_NAME = 'Automatik (Katalog)';
export const AUTOMATIK_HANDBUCH_NAME = 'Automatik (Handbuch)';

export const VERWEIS_ERLASSENDER = 'kopf:erlassender';
const PLATZHALTER_VERWEIS = 'platzhalter:';
const MERKMAL_VERWEIS = 'merkmal:';

export const platzhalterVerweis = (name: string): string => `${PLATZHALTER_VERWEIS}${name}`;
export const merkmalVerweis = (merkmal: BooleanMerkmal): string => `${MERKMAL_VERWEIS}${merkmal}`;
/** Name aus `platzhalter:<name>` — `null` bei jedem anderen Verweis. */
export const platzhalterAusVerweis = (verweis: string | null | undefined): string | null => {
    const v = String(verweis || '');
    return v.startsWith(PLATZHALTER_VERWEIS) && v.length > PLATZHALTER_VERWEIS.length
        ? v.slice(PLATZHALTER_VERWEIS.length) : null;
};
export const merkmalAusVerweis = (verweis: string | null | undefined): BooleanMerkmal | null => {
    const v = String(verweis || '');
    if (!v.startsWith(MERKMAL_VERWEIS)) return null;
    const m = v.slice(MERKMAL_VERWEIS.length);
    return m in MERKMAL_KLAERUNG ? (m as BooleanMerkmal) : null;
};

/** Anzahl der eroeffneten Fassungen seit dem Anlegen — 0 fuer die erste. */
export const fassungsIndex = (o: Pick<Bauordnung, 'historie'>): number =>
    (o.historie || []).filter(h => String(h?.was || '').startsWith(NEUE_FASSUNG_TEXT)).length;

export const automatikMarker = (o: Pick<Bauordnung, 'historie'>, katalogVersion: string = KATALOG_VERSION): string =>
    `${AUTOMATIK_PRAEFIX}${katalogVersion}:${fassungsIndex(o)}`;

export const kiMarker = (o: Pick<Bauordnung, 'historie'>): string => `${KI_PRAEFIX}${fassungsIndex(o)}`;

export const abgewaehltMarker = (bausteinId: string): string => `${ABGEWAEHLT_PRAEFIX}${bausteinId}`;

/**
 * Traegt die Historie diesen Marker? Genau der Marker oder der Marker plus
 * Leerzeichen und Zaehler — reiner Praefixvergleich waere falsch: `ki:1`
 * ist Praefix von `ki:10`.
 */
const hatMarker = (o: Pick<Bauordnung, 'historie'>, marker: string): boolean =>
    (o.historie || []).some(h => {
        const was = String(h?.was || '');
        return was === marker || was.startsWith(`${marker} `);
    });

/** Fehlt der Automatik-Lauf fuer diese Fassung und diesen Katalog? Nie bei einer freigegebenen Fassung. */
export const automatikFehlt = (o: Pick<Bauordnung, 'historie' | 'status'>): boolean =>
    o.status === 'entwurf' && !hatMarker(o, automatikMarker(o));

/** Fehlt der KI-Lauf fuer diese Fassung? Nie bei einer freigegebenen Fassung (F12). */
export const kiFehlt = (o: Pick<Bauordnung, 'historie' | 'status'>): boolean =>
    o.status === 'entwurf' && !hatMarker(o, kiMarker(o));

/** Bausteine, die jemand in „Alle Bereiche" geloescht hat — die Automatik legt sie nicht wieder an. */
export const abgewaehlteBausteine = (o: Pick<Bauordnung, 'historie'>): ReadonlySet<string> => {
    const ids = new Set<string>();
    for (const h of o.historie || []) {
        const m = /^abgewaehlt:(\S+)/.exec(String(h?.was || ''));
        if (m) ids.add(m[1]);
    }
    return ids;
};

// ═══════════════════════════════════════════════════════════════════════════
// MERKMALE — die Frage zu „Merkmal klären"
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kurzer Name und Frage je Merkmal, in der Sprache des Dialogs der
 * Planungsphase. `Record` statt Liste: faellt ein Merkmal weg oder kommt eines
 * dazu, uebersetzt die Datei nicht mehr.
 */
export const MERKMAL_KLAERUNG: Readonly<Record<BooleanMerkmal, { kurz: string; frage: string }>> = {
    mehrereArbeitgeber: { kurz: 'Mehrere Arbeitgeber', frage: 'Arbeiten Beschäftigte mehrerer Firmen auf der Baustelle?' },
    bestand: { kurz: 'Bauen im Bestand', frage: 'Wird im Bestand gebaut (Umbau, Sanierung oder Anbau)?' },
    abbruch: { kurz: 'Abbruch', frage: 'Wird etwas abgebrochen oder zurückgebaut?' },
    erdarbeiten: { kurz: 'Erdarbeiten', frage: 'Gibt es Erdarbeiten, eine Baugrube oder Gräben?' },
    strassenbaustelle: { kurz: 'Öffentlicher Verkehrsraum', frage: 'Wird im öffentlichen Verkehrsraum gearbeitet?' },
    betriebLaeuftWeiter: { kurz: 'Laufender Betrieb', frage: 'Wird das Gebäude während der Bauzeit weiter genutzt?' },
    fertigteilmontage: { kurz: 'Fertigteilmontage', frage: 'Werden Fertigteile oder schwere Bauelemente montiert?' },
    kran: { kurz: 'Kran', frage: 'Gibt es auf der Baustelle einen Kran?' },
    geruest: { kurz: 'Gerüst', frage: 'Wird ein Gerüst gebraucht?' },
    dacharbeiten: { kurz: 'Dacharbeiten', frage: 'Wird am Dach gearbeitet?' },
    freileitung: { kurz: 'Freileitung', frage: 'Gibt es eine Hochspannungsfreileitung in der Nähe?' },
    gewaesser: { kurz: 'Arbeiten am Wasser', frage: 'Wird an oder über Wasser gearbeitet?' },
    tunnelbau: { kurz: 'Tunnelbau', frage: 'Gibt es Brunnenbau, unterirdische Erdarbeiten oder Tunnelbau?' },
    taucharbeiten: { kurz: 'Taucherarbeiten', frage: 'Gibt es Taucherarbeiten?' },
    druckluft: { kurz: 'Druckluft', frage: 'Wird in Druckluft gearbeitet?' },
    sprengarbeiten: { kurz: 'Sprengarbeiten', frage: 'Wird gesprengt?' },
    strahlung: { kurz: 'Ionisierende Strahlung', frage: 'Gibt es ionisierende Strahlung mit Kontroll- oder Überwachungsbereich?' },
    gefahrstoffeAnhang2: {
        kurz: 'Besondere Gefahrstoffe',
        frage: 'Gibt es explosive oder krebserzeugende Stoffe oder biologische Arbeitsstoffe der Risikogruppe 3/4?',
    },
    tga: { kurz: 'Haustechnik', frage: 'Gibt es Haustechnik in nennenswertem Umfang (Heizung, Lüftung, Elektro)?' },
    nachtarbeit: { kurz: 'Nacht- oder Wochenendarbeit', frage: 'Wird nachts, im Schichtbetrieb oder am Wochenende gearbeitet?' },
    winterbau: { kurz: 'Winterbau', frage: 'Fällt die Bauzeit in den Winter?' },
    altlastenverdacht: { kurz: 'Altlasten', frage: 'Gibt es einen Verdacht auf Altlasten oder belasteten Boden?' },
    unternehmerOhneBeschaeftigte: {
        kurz: 'Selbstständige ohne Beschäftigte', frage: 'Arbeiten auch Selbstständige ohne eigene Beschäftigte mit?',
    },
    ortbeton: { kurz: 'Ortbeton', frage: 'Wird vor Ort geschalt, bewehrt und betoniert?' },
};

/** `Merkmal klären: Gibt es auf der Baustelle einen Kran?` */
export const merkmalKlaerenPunkt = (merkmal: BooleanMerkmal): string =>
    `Merkmal klären: ${MERKMAL_KLAERUNG[merkmal]?.frage ?? merkmal}`;

const merkmalNamen = (liste: readonly BooleanMerkmal[]): string =>
    liste.map(m => `„${MERKMAL_KLAERUNG[m]?.kurz ?? m}“`).join(' / ');

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE — was trifft zu?
// ═══════════════════════════════════════════════════════════════════════════

const regelSchluessel = (kapitel: string, bausteinId: string): string => `${kapitel}|${bausteinId}`;

/**
 * Welche Bausteine die Automatik schreibt, welche sie fragt und welche sie
 * weglaesst — in dieser Reihenfolge geprueft:
 *
 *   schon vorhanden (Kapitel + `baustein_id`)  → gar nicht (Idempotenz)
 *   abgewaehlt (F15)                           → weggelassen
 *   Stufe `optional`                           → weggelassen (wird angeboten)
 *   Kapitel „trifft nicht zu" / entfaellt      → weggelassen
 *   `nichtWenn` mit einem `true`               → weggelassen
 *   `wenn`: eines `true`                       → schreiben
 *           alle ausdruecklich `false`         → weggelassen
 *           sonst (unbekannt)                  → klaeren, je unbekanntem Merkmal
 *   nur `bedingungText`                        → weggelassen („nur bei Bedarf")
 *   ohne Bedingung                             → schreiben
 *
 * Ein fehlendes Merkmal ist NICHT „nein" (F7) — ohne Planungsphase wird
 * gefragt, nicht geraten.
 */
export const zutreffendeBausteine = (
    katalog: readonly Baustein[],
    merkmale: ProjektMerkmale,
    regeln: readonly Pick<Regel, 'kapitel' | 'baustein_id'>[],
    abgewaehlt: ReadonlySet<string>,
    ausgeschlosseneKapitel: ReadonlySet<string> = new Set(),
): BausteinAuswahl => {
    const m = (merkmale || {}) as Record<string, unknown>;
    const vorhanden = new Set(
        (regeln || []).filter(r => r.baustein_id).map(r => regelSchluessel(r.kapitel, String(r.baustein_id))),
    );
    const schreiben: Baustein[] = [];
    const weggelassen: BausteinAuswahl['weggelassen'] = [];
    const gruppen = new Map<BooleanMerkmal, Baustein[]>();

    for (const b of katalog) {
        if (vorhanden.has(regelSchluessel(b.kapitel, b.id))) continue;
        const weg = (grund: string) => weggelassen.push({ baustein: b.id, grund });

        if (abgewaehlt.has(b.id)) { weg('abgewählt: in „Alle Bereiche“ entfernt'); continue; }
        if (b.stufe === 'optional') { weg('optional: wird angeboten, nicht von selbst aufgenommen'); continue; }
        if (ausgeschlosseneKapitel.has(b.kapitel)) { weg('Kapitel als „trifft nicht zu“ markiert'); continue; }
        const def = kapitelDefinition(b.kapitel);
        if (def && !kapitelTrifftZu(def, merkmale)) { weg(`Kapitel „${def.titel}“ entfällt nach den Merkmalen`); continue; }

        const ausschluss = (b.nichtWenn || []).filter(k => m[k] === true);
        if (ausschluss.length) { weg(`entfällt: ${merkmalNamen(ausschluss)} trifft zu`); continue; }

        if (b.wenn?.length) {
            if (b.wenn.some(k => m[k] === true)) { schreiben.push(b); continue; }
            if (b.wenn.every(k => m[k] === false)) { weg(`trifft nicht zu: ${merkmalNamen(b.wenn)}`); continue; }
            for (const k of b.wenn) {
                if (m[k] === false) continue;
                const liste = gruppen.get(k) ?? [];
                liste.push(b);
                gruppen.set(k, liste);
            }
            continue;
        }
        if (b.bedingungText?.trim()) { weg(`nur bei Bedarf: ${b.bedingungText.trim()}`); continue; }
        schreiben.push(b);
    }

    return {
        schreiben,
        klaeren: Array.from(gruppen, ([merkmal, bausteine]) => ({ merkmal, bausteine })),
        weggelassen,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// PLATZHALTER — Werte und Fuellen
// ═══════════════════════════════════════════════════════════════════════════

/** ISO-Tag (oder Zeitstempel mit ISO-Tag vorn) → TT.MM.JJJJ; sonst leer.
 *  Exportiert, weil das Deckblatt (`dokument/baueDokument.ts`) dieselbe Form
 *  drucken muss wie die Regeltexte — zwei Datumsformen in einem Dokument
 *  fielen dem Leser zuerst auf. */
export const tagDeutsch = (iso: string | null | undefined): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '').trim());
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/** „Rev. 00" → „00" — der Baustein schreibt selbst „Revision {revision_nr}". */
const revisionAus = (label: string | null | undefined): string =>
    String(label || '').trim().replace(/^rev(?:ision)?\.?\s*/i, '').trim();

const belegt = (a: Angabe<string> | null | undefined): string =>
    a && a.stand === 'belegt' ? String(a.wert ?? '').trim() : '';

const UHRZEIT = String.raw`(\d{1,2})(?:[:.](\d{2}))?`;
const SPANNE = new RegExp(`${UHRZEIT}\\s*(?:uhr)?\\s*(?:\\u2013|\\u2014|-|bis)\\s*${UHRZEIT}\\s*(?:uhr)?`, 'gi');

/**
 * Beginn und Ende aus dem Arbeitszeiten-Feld des Handbuchs — nur, wenn es
 * EINDEUTIG ist: genau eine Spanne („7:00 – 17:00", „7 bis 17 Uhr",
 * „07.00-17.00 Uhr") und sonst keine Ziffer im Text. „Mo–Fr 7–17, Sa 8–13"
 * ergibt `null`; dann fragt die Karte, statt eine der Zeiten zu waehlen.
 */
export const arbeitszeitAus = (text: string | null | undefined): { beginn: string; ende: string } | null => {
    const s = String(text || '');
    const treffer = Array.from(s.matchAll(SPANNE));
    if (treffer.length !== 1) return null;
    const t = treffer[0];
    const start = t.index ?? 0;
    const rest = `${s.slice(0, start)} ${s.slice(start + t[0].length)}`;
    if (/\d/.test(rest)) return null;
    const h1 = Number(t[1]);
    const m1 = Number(t[2] ?? 0);
    const h2 = Number(t[3]);
    const m2 = Number(t[4] ?? 0);
    if (h1 > 24 || h2 > 24 || m1 > 59 || m2 > 59) return null;
    if (h1 * 60 + m1 >= h2 * 60 + m2) return null;
    const uhr = (h: number, min: number) => `${h}:${String(min).padStart(2, '0')} Uhr`;
    return { beginn: uhr(h1, m1), ende: uhr(h2, m2) };
};

const TAGE = '(?:\\u2013|\\u2014|-|bis)\\s*';
const MO_FR = new RegExp(String.raw`(?:^|[^a-zäöüß])mo(?:ntag)?s?\.?\s*${TAGE}fr(?:eitag)?s?\.?(?![a-zäöüß])`, 'i');
const MO_SA = new RegExp(String.raw`(?:^|[^a-zäöüß])mo(?:ntag)?s?\.?\s*${TAGE}sa(?:mstag)?s?\.?(?![a-zäöüß])`, 'i');
const ANDERE_TAGE = /(?:^|[^a-zäöüß])(?:sa|samstags?|so|sonntags?|sonn|wochenende|feiertags?)(?![a-zäöüß])/i;

/**
 * Die Arbeitstage aus dem Arbeitszeiten-Feld — „Mo–Fr" / „Montag bis Freitag"
 * oder „Mo–Sa"; in der Form, die in den Baustein passt („an den Werktagen
 * Montag bis Freitag in Betrieb"). Nennt der Text noch andere Tage, `null`.
 */
export const arbeitstageAus = (text: string | null | undefined): string | null => {
    const s = String(text || '');
    const fr = MO_FR.exec(s);
    const sa = MO_SA.exec(s);
    if (!!fr === !!sa) return null;
    const t = (fr ?? sa) as RegExpExecArray;
    const rest = `${s.slice(0, t.index)} ${s.slice(t.index + t[0].length)}`;
    if (ANDERE_TAGE.test(rest)) return null;
    return fr ? 'den Werktagen Montag bis Freitag' : 'den Werktagen Montag bis Samstag';
};

/**
 * Je automatisch gefuellten Platzhalter die Stelle, aus der er kommt. Die
 * Quellen stehen in `platzhalter.ts`; der Test haelt fest, dass jeder Name
 * mit automatischer Quelle hier steht und kein `vorgabe`/`mensch`.
 *
 * Die dynamischen Platzhalter des Kopfsatzes stehen NICHT hier: der Lauf
 * setzt sie nicht ein (`fuellePlatzhalter`), ihr Wert wird erst beim Druck
 * gelesen (`loeseKopfPlatzhalter`). Ein Eintrag hier waere eine zweite,
 * auseinanderlaufende Herleitung desselben Werts.
 */
const WERT_AUS: Readonly<Record<string, (q: AutomatikQuellen) => string | null | undefined>> = {
    baustelle_bezeichnung: q => q.projekt.name || belegt(q.steckbrief?.projekt.name),
    bauherr: q => belegt(q.steckbrief?.bauherr.name),
    sigeko: q => q.buero.firma,
    sammelplatz: q => q.handbuch?.ordnung?.sammelplatz,
    arbeitszeit_beginn: q => arbeitszeitAus(q.handbuch?.ordnung?.arbeitszeiten)?.beginn,
    arbeitszeit_ende: q => arbeitszeitAus(q.handbuch?.ordnung?.arbeitszeiten)?.ende,
    arbeitstage: q => arbeitstageAus(q.handbuch?.ordnung?.arbeitszeiten),
    krankenhaus: q => [q.notrufplan?.krankenhaus?.name, q.notrufplan?.krankenhaus?.adresse]
        .map(x => String(x || '').trim()).filter(Boolean).join(', '),
    durchgangsarzt: q => q.notrufplan?.durchgangsarzt?.name,
    durchgangsarzt_anschrift: q => q.notrufplan?.durchgangsarzt?.adresse,
};

/** Die Namen, die die Automatik fuellen kann — fuer den Abgleich mit der Tabelle. */
export const AUTOMATISCH_GEFUELLT: readonly string[] = Object.keys(WERT_AUS);

/**
 * Die belegten Werte der Platzhalter — nur, was ein Projektdatum traegt.
 * Leere Werte fehlen im Ergebnis; `vorgabe` und `mensch` liefern nie einen,
 * die dynamischen des Kopfsatzes auch nicht (siehe `WERT_AUS`).
 */
export const platzhalterWerte = (q: AutomatikQuellen): Record<string, string> => {
    const werte: Record<string, string> = {};
    for (const [name, aus] of Object.entries(WERT_AUS)) {
        const wert = String(aus(q) ?? '').trim();
        if (wert) werte[name] = wert;
    }
    return werte;
};

/**
 * Ersetzt `{name}` durch den Wert; was keinen Wert hat, wird `[Kurz]` (F3) —
 * die Form, die `platzhalterIn` findet und die Freigabe anhaelt.
 *
 * Dynamische Platzhalter bleiben als `{name}` stehen und landen nicht in
 * `offen` — gleich, ob `werte` einen Wert fuer sie traegt. Die Entscheidung
 * faellt HIER und nur hier: das ist die einzige Stelle, an der der Lauf
 * Text schreibt; `platzhalterWerte` liefert fuer sie ohnehin nichts, und
 * ohne diese Ausnahme wuerde ein fehlender Wert zu `[Revision]` plus offenem
 * Punkt.
 */
export const fuellePlatzhalter = (
    wortlaut: string,
    werte: Record<string, string>,
): { text: string; offen: string[] } => {
    const offen: string[] = [];
    const text = String(wortlaut || '').replace(/\{([a-z0-9_]+)\}/g, (ganz: string, name: string) => {
        if (istDynamisch(name)) return ganz;
        const wert = String(werte[name] ?? '').trim();
        if (wert) return wert;
        if (!offen.includes(name)) offen.push(name);
        return `[${nameZuKurz(name)}]`;
    });
    return { text, offen };
};

/** So steht ein Kopfsatzfeld im Satz; fehlt der Eintrag, steht der Wert, wie er ist. */
const KOPF_FORM: Partial<Record<KopfFeld, (roh: string) => string>> = {
    stand: tagDeutsch,
    geltung_von: tagDeutsch,
    geltung_bis: tagDeutsch,
    version_label: revisionAus,
};

/**
 * Loest die dynamischen Platzhalter eines Wortlauts aus dem Kopfsatz auf —
 * fuer den Druck (`baueDokument`), nie zum Speichern.
 *
 *   Datum     TT.MM.JJJJ (Stand, Geltungsbeginn, -ende)
 *   Revision  ohne „Rev. " — der Baustein schreibt selbst „Revision {revision_nr}"
 *   leer      `[Kurz]` — ein Entwurfsdruck zeigt die Luecke; die Freigabe
 *             haelt an einem leeren Feld ohnehin an
 *
 * Laesst sich ein belegtes Feld nicht umformen (Datum in anderer Schreibweise,
 * nur „Rev."), steht es, wie es ist — die Freigabe prueft das Feld, nicht die
 * Form; ein `[Stand]` trotz gesetztem Stand waere eine Luecke, die niemand
 * mehr meldet. Andere Platzhalter, `{…}` wie `[…]`, bleiben unberuehrt.
 */
export const loeseKopfPlatzhalter = (wortlaut: string, o: Pick<Bauordnung, KopfFeld>): string =>
    String(wortlaut || '').replace(/\{([a-z0-9_]+)\}/g, (ganz: string, name: string) => {
        if (!istDynamisch(name)) return ganz;
        const feld = KOPF_FELD_JE_PLATZHALTER[name];
        const roh = String(o[feld] ?? '').trim();
        const wert = (KOPF_FORM[feld]?.(roh) || roh).trim();
        return wert || `[${nameZuKurz(name)}]`;
    });

const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Treffer von `platzhalterIn` → Namen, ohne Doppelte. Unbekanntes `[…]` behaelt seinen Text als Namen. */
const namenAus = (treffer: readonly string[]): string[] =>
    Array.from(new Set(treffer.map(t => {
        const innen = t.slice(1, -1).trim();
        return t.startsWith('{') ? innen : (kurzZuName(innen) ?? innen);
    })));

/** Die Namen der Platzhalter im Text — beide Formen, ohne Doppelte. Unbekanntes `[…]` behaelt seinen Text als Namen. */
export const platzhalterNamenIn = (text: string): string[] => namenAus(platzhalterIn(text));

/**
 * Die Namen der OFFENEN Platzhalter im Text — wie `platzhalterNamenIn`, ohne
 * die dynamischen `{name}` des Kopfsatzes (`offenePlatzhalterIn`). Genau zu
 * diesen Namen gehoert ein offener Punkt.
 */
export const offenePlatzhalterNamenIn = (text: string): string[] => namenAus(offenePlatzhalterIn(text));

/**
 * Setzt eine Antwort an die Stelle eines Platzhalters — in jeder Form, in der
 * er im Text stehen kann (`{name}`, `[Kurz]`, `[name]`), Gross-/Kleinschreibung
 * egal. Die Antwort wird woertlich eingesetzt (kein `$`-Muster).
 */
export const ersetzePlatzhalter = (wortlaut: string, name: string, wert: string): string => {
    const formen = [
        `\\{${escape(name)}\\}`,
        `\\[\\s*${escape(nameZuKurz(name))}\\s*\\]`,
        `\\[\\s*${escape(name)}\\s*\\]`,
    ];
    return String(wortlaut || '').replace(new RegExp(formen.join('|'), 'gi'), () => wert);
};

// ═══════════════════════════════════════════════════════════════════════════
// KOPFSATZ
// ═══════════════════════════════════════════════════════════════════════════

interface KopfLage {
    patch: Partial<Bauordnung>;
    /** Bauherr UND Dritter belegt, Erlassender leer: das regelt der Vertrag — Frage statt Raten. */
    erlassenderFrage: string | null;
}

/**
 * Nur leere Felder, nur `belegt`e Werte (wie die Folgeunterlage). Der
 * Erlassende nur, wenn weder Firma noch Rolle gesetzt ist und GENAU einer von
 * Bauherr und Drittem belegt ist. `erzeugt_ordnung` nur, wenn gar kein
 * Dokument gewaehlt ist — wer bewusst nur den HSE-Plan erzeugt, behaelt das.
 */
const kopfLage = (q: AutomatikQuellen): KopfLage => {
    const o = q.ordnung;
    const patch: Partial<Bauordnung> = {};
    if (o.status !== 'entwurf') return { patch, erlassenderFrage: null };

    if (!o.stand) patch.stand = q.heuteIso;
    if (!String(o.version_label ?? '').trim()) patch.version_label = 'Rev. 00';
    if (!o.erzeugt_ordnung && !o.erzeugt_hse) patch.erzeugt_ordnung = true;

    const sb = q.steckbrief;
    if (!o.geltung_ort.trim()) {
        const ort = belegt(sb?.projekt.adresse) || q.projekt.adresse.trim();
        if (ort) patch.geltung_ort = ort;
    }

    let erlassenderFrage: string | null = null;
    if (sb) {
        const vorschlaege = grundlagenVorschlaege(sb, o).filter(v => !v.ersetzt && v.stand === 'belegt');
        if (!o.erlassender_firma.trim() && !o.erlassender_rolle) {
            const erlassende = vorschlaege.filter(v => v.feld === 'erlassender');
            if (erlassende.length === 1) {
                Object.assign(patch, erlassende[0].patch);
            } else if (erlassende.length > 1) {
                const bauherr = erlassende.find(v => v.id === 'erlassender_bauherr')?.wertText ?? '';
                const dritter = erlassende.find(v => v.id === 'erlassender_dritter')?.wertText ?? '';
                erlassenderFrage = `Wer erlässt die Baustellenordnung: der Bauherr „${bauherr}“ oder „${dritter}“ `
                    + 'als Dritter nach § 4 BaustellV?';
            }
        }
        for (const feld of ['geltung_von', 'geltung_bis'] as const) {
            if (o[feld]) continue;
            const v = vorschlaege.find(x => x.feld === feld);
            if (v) Object.assign(patch, v.patch);
        }
    }
    return { patch, erlassenderFrage };
};

// ═══════════════════════════════════════════════════════════════════════════
// ROLLEN
// ═══════════════════════════════════════════════════════════════════════════

const ERLASSENDER_ROLLE: Readonly<Record<ErlassenderRolle, string>> = {
    bauherr: 'Bauherr',
    gu: 'Generalunternehmer',
    bauleitung: 'Bauleitung',
};

const vergleich = (s: string | null | undefined): string => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Die Organisation aus den Projektbeteiligten (E3): Erlassender, Bauherr und
 * Dritter nach § 4 (jeweils nur, wenn vom Erlassenden verschieden), SiGeKo
 * Ausfuehrung (eigenes Buero). Weisungsbefugnis immer „keine" — wer sie
 * jemandem zuschreiben will, tut das ausdruecklich. Doppelerkennung Rolle +
 * Firma gegen die vorhandenen und die eigenen neuen Zeilen.
 */
export const rollenAusProjekt = (q: AutomatikQuellen): NeueRolle[] => {
    if (q.ordnung.status !== 'entwurf') return [];
    const kopf = { ...q.ordnung, ...kopfLage(q).patch };
    const sb = q.steckbrief;
    const vorhanden = new Set(q.bundle.rollen.map(r => `${vergleich(r.rolle)}|${vergleich(r.firma)}`));
    let sortierung = q.bundle.rollen.reduce((max, r) => Math.max(max, (r.sortierung ?? 0) + 1), 0);
    const neu: NeueRolle[] = [];

    const dazu = (rolle: string, firma: string, name: string, funktion: string, erreichbarkeit = '') => {
        if (!firma.trim() && !name.trim()) return;
        const schluessel = `${vergleich(rolle)}|${vergleich(firma)}`;
        if (vorhanden.has(schluessel)) return;
        vorhanden.add(schluessel);
        neu.push({
            rolle, funktion: funktion.trim(), firma: firma.trim(), name: name.trim(), erreichbarkeit: erreichbarkeit.trim(),
            weisungsbefugnis: 'keine', befugnis_umfang: '', bestellt_am: null, sortierung: sortierung++,
        });
    };

    const erlassender = kopf.erlassender_firma.trim();
    if (erlassender) {
        dazu(
            kopf.erlassender_rolle ? ERLASSENDER_ROLLE[kopf.erlassender_rolle] : 'Erlassender',
            erlassender, kopf.erlassender_name, kopf.erlassender_funktion,
        );
    }
    const bauherr = belegt(sb?.bauherr.name);
    if (bauherr && vergleich(bauherr) !== vergleich(erlassender)) {
        dazu('Bauherr', bauherr, '', '', belegt(sb?.bauherr.telefon));
    }
    const dritter = belegt(sb?.dritter.name);
    if (dritter && vergleich(dritter) !== vergleich(erlassender)) {
        dazu(belegt(sb?.dritter.rolle) || 'Dritter nach § 4 BaustellV', dritter, '', '');
    }
    dazu('SiGeKo Ausführungsphase', q.buero.firma, q.buero.handelnder, 'sigeko_ausfuehrung');
    return neu;
};

// ═══════════════════════════════════════════════════════════════════════════
// LUECKEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Offene Punkte aus den BESTEHENDEN Regeln.
 *
 *   neu        je bestaetigter Regel und Platzhalter (`[…]` oder `{…}`) ohne
 *              offene Luecke derselben Regel — eine erledigte zaehlt nicht:
 *              steht der Platzhalter noch da, ist er nicht erledigt (F4);
 *   erledigen  offene Luecken `platzhalter:…`, deren Regel den Platzhalter
 *              nicht mehr traegt oder nicht mehr da ist — erledigt, nicht
 *              geloescht (H.2: die Feststellung bleibt stehen).
 *
 * Ein dynamisches `{name}` des Kopfsatzes ist kein offener Platzhalter
 * (`offenePlatzhalterNamenIn`): es bekommt keinen Punkt, und ein alter Punkt
 * dazu wird erledigt — dieselbe Grenze wie beim Freigabe-Hindernis.
 */
export const lueckenAusRegeln = (
    regeln: readonly Regel[],
    luecken: readonly Luecke[],
): { neu: NeueLuecke[]; erledigen: string[] } => {
    const nachId = new Map(regeln.map(r => [r.id, r]));
    const offen = luecken.filter(l => !l.erledigt_am);
    const erledigen: string[] = [];
    for (const l of offen) {
        const name = platzhalterAusVerweis(l.verweis);
        if (name === null) continue;
        const r = l.regel_id ? nachId.get(l.regel_id) : undefined;
        if (!r || !offenePlatzhalterNamenIn(r.wortlaut).includes(name)) erledigen.push(l.id);
    }

    const bleibt = new Set(
        offen.filter(l => !erledigen.includes(l.id)).map(l => `${l.regel_id ?? ''}|${l.verweis}`),
    );
    const neu: NeueLuecke[] = [];
    for (const r of regeln) {
        if (!istBestaetigt(r)) continue;
        for (const name of offenePlatzhalterNamenIn(r.wortlaut)) {
            const verweis = platzhalterVerweis(name);
            const schluessel = `${r.id}|${verweis}`;
            if (bleibt.has(schluessel)) continue;
            bleibt.add(schluessel);
            neu.push({
                kapitel: r.kapitel,
                punkt: platzhalterDefinition(name).frage,
                verweis, pflicht: true, herkunft: 'automatisch',
                platzhalter: name, regelId: r.id,
            });
        }
    }
    return { neu, erledigen };
};

// ═══════════════════════════════════════════════════════════════════════════
// REGELN AUS BAUSTEINEN
// ═══════════════════════════════════════════════════════════════════════════

/** Naechste freie Sortierung je Kapitel. */
const naechsteSortierung = (regeln: readonly Pick<Regel, 'kapitel' | 'sortierung'>[]): Map<string, number> => {
    const naechste = new Map<string, number>();
    for (const r of regeln) {
        naechste.set(r.kapitel, Math.max(naechste.get(r.kapitel) ?? 0, (r.sortierung ?? 0) + 1));
    }
    return naechste;
};

const alsNeueRegeln = (
    bausteine: readonly Baustein[],
    werte: Record<string, string>,
    naechste: Map<string, number>,
): NeueRegel[] => bausteine.map(b => {
    const { text, offen } = fuellePlatzhalter(b.wortlaut, werte);
    const sortierung = naechste.get(b.kapitel) ?? 0;
    naechste.set(b.kapitel, sortierung + 1);
    return {
        kapitel: b.kapitel, baustein_id: b.id, wortlaut: text, art: b.art, quelle: 'katalog',
        sortierung, offenePlatzhalter: offen,
    };
});

const ausgeschlosseneKapitel = (bundle: BauordnungBundle): Set<string> =>
    new Set(bundle.abschnitte.filter(a => !a.gezogen).map(a => a.kapitel));

/**
 * Die Regeln zu einer Antwort auf „Merkmal klären" — fuer
 * `bauordnungService.klaerungBeantworten`.
 *
 *   ja    die Bausteine der Gruppe, die mit diesem Merkmal zutreffen;
 *   nein  die Bausteine der Gruppe, die damit sicher entfallen (alle ihre
 *         Merkmale `false`). Ein Baustein, der noch an einem zweiten,
 *         unbekannten Merkmal haengt (ODER), bleibt fuer dessen Frage stehen
 *         — sein Abwahl-Marker wuerde ihn sonst auch dort unterdruecken.
 */
export const klaerungsRegeln = (q: AutomatikQuellen, merkmal: BooleanMerkmal, ja: boolean): NeueRegel[] => {
    const katalog = q.katalog ?? KATALOG;
    const abgewaehlt = abgewaehlteBausteine(q.ordnung);
    const aus = ausgeschlosseneKapitel(q.bundle);
    const heute = zutreffendeBausteine(katalog, q.merkmale, q.bundle.regeln, abgewaehlt, aus);
    const gruppe = new Set((heute.klaeren.find(g => g.merkmal === merkmal)?.bausteine ?? []).map(b => b.id));
    if (!gruppe.size) return [];

    const danach = zutreffendeBausteine(katalog, { ...q.merkmale, [merkmal]: ja }, q.bundle.regeln, abgewaehlt, aus);
    const entfallen = new Set(danach.weggelassen.map(w => w.baustein));
    const bausteine = ja
        ? danach.schreiben.filter(b => gruppe.has(b.id))
        : katalog.filter(b => gruppe.has(b.id) && entfallen.has(b.id));
    return alsNeueRegeln(bausteine, platzhalterWerte(q), naechsteSortierung(q.bundle.regeln));
};

/** Wortlaut fuer den Doppelvergleich: Leerraum zusammengezogen, Gross/Klein egal. */
const vergleichsWortlaut = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Regeln, die in der Datenbank doppelt stehen — die juengeren (F17). Zwei
 * Arten, beide Folge zweier gleichzeitiger Sitzungen:
 *  - Katalogregeln: gleiches Kapitel, gleicher Baustein (zwei Automatik-Laeufe);
 *  - KI-Regeln: gleiches Kapitel, gleicher Wortlaut (zwei KI-Laeufe, deren
 *    Doppelerkennung nur den Stand beim Start kannte).
 */
export const doppelteRegeln = (regeln: readonly Regel[]): string[] => {
    const gesehen = new Set<string>();
    const doppelt: string[] = [];
    const reihenfolge = regeln
        .map((r, i) => ({ r, i }))
        .filter(x => x.r.baustein_id || x.r.quelle === 'ki')
        .sort((a, b) => (a.r.sortierung - b.r.sortierung) || (a.i - b.i));
    for (const { r } of reihenfolge) {
        const k = r.baustein_id
            ? regelSchluessel(r.kapitel, String(r.baustein_id))
            : `ki|${r.kapitel}|${vergleichsWortlaut(r.wortlaut)}`;
        if (gesehen.has(k)) doppelt.push(r.id);
        else gesehen.add(k);
    }
    return doppelt;
};

/**
 * Rollen, die ZEICHENGLEICH doppelt stehen — die juengeren. Dieselbe Ursache
 * wie bei den Regeln: zwei Automatik-Laeufe schreiben beide ihr Paket, und
 * fuer Rollen gibt es keinen Eindeutigkeitsindex.
 *
 * Verglichen werden Rolle, Funktion, Firma, Name UND Erreichbarkeit: zwei
 * Ersthelfer derselben Firma mit verschiedenen Namen sind zwei Rollen, die
 * ein Mensch bewusst angelegt hat — nur eine Kopie der Automatik gleicht der
 * anderen in jedem Feld (Opus-Review 02.11.2026).
 */
export const doppelteRollen = (
    rollen: readonly Pick<Rolle, 'id' | 'rolle' | 'funktion' | 'firma' | 'name' | 'erreichbarkeit' | 'sortierung'>[],
): string[] => {
    const gesehen = new Set<string>();
    const doppelt: string[] = [];
    const reihenfolge = rollen
        .map((r, i) => ({ r, i }))
        .sort((a, b) => (a.r.sortierung - b.r.sortierung) || (a.i - b.i));
    for (const { r } of reihenfolge) {
        if (!r.rolle.trim()) continue;
        const k = [r.rolle, r.funktion, r.firma, r.name, r.erreichbarkeit].map(vergleichsWortlaut).join('|');
        if (gesehen.has(k)) doppelt.push(r.id);
        else gesehen.add(k);
    }
    return doppelt;
};

// ═══════════════════════════════════════════════════════════════════════════
// HANDBUCH — nur was der Katalog nicht schon traegt
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Handbuchfelder, deren Inhalt die Automatik als Platzhalterwert in einen
 * Baustein setzt. Steht der Baustein (neu oder schon vorhanden) und sind alle
 * Werte gelesen, waere die Handbuchzeile eine Doppelung im Dokument
 * („Arbeitszeiten: Mo–Fr 7–17 Uhr" neben „Die Baustelle ist von 7:00 Uhr bis
 * 17:00 Uhr … in Betrieb").
 */
const HANDBUCH_IM_KATALOG: Partial<Record<HandbuchFeld, readonly string[]>> = {
    arbeitszeiten: ['arbeitszeit_beginn', 'arbeitszeit_ende'],
    sammelplatz: ['sammelplatz'],
};

const uebernahmeOhneDoppelung = (
    u: HandbuchUebernahme,
    werte: Record<string, string>,
    getragen: ReadonlySet<string>,
): HandbuchUebernahme => {
    const verbraucht = new Set(HANDBUCH_FELDER.filter(feld => {
        const namen = HANDBUCH_IM_KATALOG[feld];
        return !!namen?.length && namen.every(n => werte[n] && getragen.has(n));
    }));
    if (!verbraucht.size) return u;
    return {
        ...u,
        regeln: u.regeln.filter(r => !verbraucht.has(r.feld)),
        jeFeld: u.jeFeld.filter(f => !verbraucht.has(f.feld)),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS PAKET
// ═══════════════════════════════════════════════════════════════════════════

const anzahl = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

const leeresPaket = (o: Bauordnung): AutomatikPaket => ({
    kopfPatch: {}, handbuch: null, regeln: [], rollen: [], luecken: [], erledigen: [],
    ersteLueckenNr: 1, klaeren: [], weggelassen: [],
    zaehler: { regeln: 0, rollen: 0, luecken: 0, klaeren: 0, handbuchZeilen: 0 },
    leer: true,
    historie: automatikMarker(o),
});

/** Platzhalter einer Regel ohne Datenbankzeile — fuer die Doppelerkennung der Handbuch-Uebernahme. */
const alsVorlaeufigeRegel = (r: NeueRegel, o: Bauordnung): Regel => ({
    id: '', project_id: o.project_id, ordnung_id: o.id, kapitel: r.kapitel, baustein_id: r.baustein_id,
    katalog_version: KATALOG_VERSION, wortlaut: r.wortlaut, art: r.art, zielgruppe: [], rhythmus: '',
    verantwortlich: '', sige_bezug: '', quelle: 'katalog', bestaetigt_am: null, bestaetigt_von_name: '',
    sortierung: r.sortierung,
});

/**
 * Das ganze Paket des Automatik-Laufs — rein.
 *
 * Reihenfolge der Sortierung je Kapitel: vorhandene Zeilen, dann die
 * Katalogbausteine in Katalogreihenfolge, dann die Handbuchzeilen.
 *
 * NACHZUG (§3.1 c): Eine eigene Funktion braucht es nicht. Ist
 * `automatikFehlt(o) === false` und liefert dieses Paket trotzdem
 * `regeln.length > 0`, sind das die Nachzuegler — Bausteine, deren Merkmal
 * seit dem Lauf `true` geworden ist (Planungsphase nachgezogen). Der Dialog
 * zeigt sie als Karte „Neu dazu", statt sie still zu schreiben; schreibt er
 * sie, traegt die Historie „Automatik nachgezogen" statt eines zweiten
 * Markers.
 */
export const baueAutomatikEntwurf = (q: AutomatikQuellen): AutomatikPaket => {
    const o = q.ordnung;
    if (o.status !== 'entwurf') return leeresPaket(o);
    const katalog = q.katalog ?? KATALOG;

    const { patch: kopfPatch, erlassenderFrage } = kopfLage(q);
    const werte = platzhalterWerte(q);
    const auswahl = zutreffendeBausteine(
        katalog, q.merkmale, q.bundle.regeln, abgewaehlteBausteine(o), ausgeschlosseneKapitel(q.bundle),
    );

    // ─── Regeln aus dem Katalog ──────────────────────────────────────────
    const naechste = naechsteSortierung(q.bundle.regeln);
    const regeln = alsNeueRegeln(auswahl.schreiben, werte, naechste);

    // ─── Handbuch (E7): nur mit Inhalt, nur was der Katalog nicht traegt ─
    let handbuch: HandbuchUebernahme | null = null;
    const roh = uebernahmeAusHandbuch(
        { ordnung: o, regeln: [...q.bundle.regeln, ...regeln.map(r => alsVorlaeufigeRegel(r, o))] },
        q.handbuch,
    );
    if (roh) {
        const nachId = new Map(katalog.map(b => [b.id, b]));
        const getragen = new Set<string>();
        for (const id of [...q.bundle.regeln.map(r => r.baustein_id), ...regeln.map(r => r.baustein_id)]) {
            for (const n of (id && nachId.get(id)?.platzhalter) || []) getragen.add(n);
        }
        const bereinigt = uebernahmeOhneDoppelung(roh, werte, getragen);
        // Ohne eine Ordnungszeile keine Uebernahme: sie laeuft genau einmal
        // und setzt den Marker — ein Handbuch, das nur ein Modell fuehrt,
        // verbrauchte sie, bevor seine Ordnungsfelder gefuellt sind.
        if (bereinigt.regeln.length) handbuch = bereinigt;
    }

    // ─── Offene Punkte ───────────────────────────────────────────────────
    const luecken: NeueLuecke[] = [];
    if (erlassenderFrage && !q.bundle.luecken.some(l => l.verweis === VERWEIS_ERLASSENDER)) {
        luecken.push({
            kapitel: '', punkt: erlassenderFrage, verweis: VERWEIS_ERLASSENDER, pflicht: true, herkunft: 'automatisch',
        });
    }
    for (const r of regeln) {
        for (const name of r.offenePlatzhalter) {
            luecken.push({
                kapitel: r.kapitel, punkt: platzhalterDefinition(name).frage, verweis: platzhalterVerweis(name),
                pflicht: true, herkunft: 'automatisch', platzhalter: name, bausteinId: r.baustein_id,
            });
        }
    }
    const ausBestand = lueckenAusRegeln(q.bundle.regeln, q.bundle.luecken);
    luecken.push(...ausBestand.neu);

    // Eine erledigte Merkmal-Frage ist beantwortet — die Antwort steht als
    // Regeln (ja) oder Abwahl-Marker (nein). Sie wird nicht neu gestellt,
    // auch wenn ein ODER-Baustein noch an einem zweiten Merkmal haengt.
    const klaeren = auswahl.klaeren.filter(g =>
        !q.bundle.luecken.some(l => l.verweis === merkmalVerweis(g.merkmal) && l.erledigt_am));
    for (const g of klaeren) {
        if (q.bundle.luecken.some(l => l.verweis === merkmalVerweis(g.merkmal))) continue;
        luecken.push({
            kapitel: g.bausteine[0]?.kapitel ?? '', punkt: merkmalKlaerenPunkt(g.merkmal),
            verweis: merkmalVerweis(g.merkmal), pflicht: true, herkunft: 'automatisch', merkmal: g.merkmal,
        });
    }

    // ─── Organisation ────────────────────────────────────────────────────
    const rollen = rollenAusProjekt(q);

    const zaehler = {
        regeln: regeln.length,
        rollen: rollen.length,
        luecken: luecken.length,
        klaeren: klaeren.length,
        handbuchZeilen: handbuch?.regeln.length ?? 0,
    };
    const leer = !Object.keys(kopfPatch).length && !handbuch && !regeln.length && !rollen.length
        && !luecken.length && !ausBestand.erledigen.length;

    const teile = [
        anzahl(zaehler.regeln, 'Regelung', 'Regelungen'),
        anzahl(zaehler.rollen, 'Rolle', 'Rollen'),
        anzahl(zaehler.luecken, 'offener Punkt', 'offene Punkte'),
    ];
    if (zaehler.handbuchZeilen) teile.push(anzahl(zaehler.handbuchZeilen, 'Zeile', 'Zeilen') + ' aus dem Handbuch');
    const historie = automatikFehlt(o)
        ? `${automatikMarker(o)} · ${teile.join(', ')}`
        : `Automatik nachgezogen · ${teile.join(', ')}`;

    return {
        kopfPatch,
        handbuch,
        regeln,
        rollen,
        luecken,
        erledigen: ausBestand.erledigen,
        ersteLueckenNr: naechsteLueckenNummer(q.bundle.luecken),
        klaeren: klaeren.map(g => ({ merkmal: g.merkmal, bausteine: g.bausteine.map(b => b.id) })),
        weggelassen: auswahl.weggelassen,
        zaehler,
        leer,
        historie,
    };
};
